import { createContext, useState, useMemo, useContext, useCallback, useEffect, useRef } from 'react';
import * as calidadApi from '../../services/calidadApi';

const CalidadContext = createContext();

function esMismoDia(a, b) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

// Una fila de Calidad = una ocurrencia del defecto. `cantidad` no es parte del
// contrato (Calidad-ContratoFront.md §7.1) y el backend siempre persiste 0 —
// `unicos`/`defectoLider` cuentan filas, no leen ese campo.
//
// `total` (piezas scrap) NO es igual a filas: un reporte tiene una cabecera
// Serie/Lote compartida y N líneas (una pieza con varios defectos genera
// varias filas), así que se cuenta por N° de serie/lote distinto
// (normalizado con trim+mayúsculas). Las filas sin serie no se pueden
// agrupar entre sí, así que cada una cuenta como una pieza propia.
export function calcularResumen(registros, catalogoDefectos) {
    const series = new Set();
    let sinSerie = 0;
    registros.forEach((r) => {
        const serie = typeof r.loteoSerie === 'string' ? r.loteoSerie.trim().toUpperCase() : '';
        if (serie) {
            series.add(serie);
        } else {
            sinSerie += 1;
        }
    });
    const total = series.size + sinSerie;

    const defectoIds = new Set(registros.map((r) => r.defecto));
    const unicos = defectoIds.size;

    const counts = {};
    registros.forEach((r) => {
        const defObj = catalogoDefectos.find((d) => d.codigo === r.defecto);
        const label = defObj ? defObj.label : `Defecto ${r.defecto}`;
        counts[label] = (counts[label] || 0) + 1;
    });

    let defectoLider = '—';
    let maxCount = 0;
    Object.entries(counts).forEach(([label, count]) => {
        if (count > maxCount) {
            maxCount = count;
            defectoLider = label;
        }
    });

    return { total, unicos, defectoLider };
}

// ── Provider ─────────────────────────────────────────────────────────────
export function CalidadProvider(props) {
    const [selectedFecha, setSelectedFecha] = useState(new Date());

    // Área → Línea → Corrida
    const [areas, setAreas] = useState([]);
    const [selectedArea, setSelectedAreaState] = useState(null);
    const [lineas, setLineas] = useState([]);
    const [selectedLinea, setSelectedLineaState] = useState(null);
    const [corridas, setCorridas] = useState([]);
    const [selectedCorrida, setSelectedCorridaState] = useState(null);
    const [loadingCorridas, setLoadingCorridas] = useState(false);

    // ── Catálogos dinámicos ──────────────────────────────────────────────
    const [catalogoDefectos, setCatalogoDefectos] = useState([]);
    const [catalogoCausas, setCatalogoCausas] = useState([]);
    const [catalogoPartes, setCatalogoPartes] = useState([]);
    const [parteDefectoPairs, setParteDefectoPairs] = useState([]);
    const [loadingCatalogos, setLoadingCatalogos] = useState(true);

    const [registros, setRegistros] = useState([]);
    const [loadingRegistros, setLoadingRegistros] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [errorGuardado, setErrorGuardado] = useState(null);
    const [errorAccion, setErrorAccion] = useState(null);
    const [resultadoParcial, setResultadoParcial] = useState(null);
    const [formOpen, setFormOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null);

    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const corridaId = useMemo(() => selectedCorrida?.corrida ?? selectedCorrida?.id ?? null, [selectedCorrida]);

    // ── Cargar áreas y catálogos al montar ────────────────────────────────
    useEffect(() => {
        const controller = new AbortController();
        async function cargarInicial() {
            setLoadingCatalogos(true);
            const [areasR, defectosR, causasR, partesR] = await Promise.allSettled([
                calidadApi.getAreas(controller.signal),
                calidadApi.getCatalogoEnum('DefectosCalidad', controller.signal),
                calidadApi.getCatalogoEnum('CausaRaizCalidad', controller.signal),
                calidadApi.getPartes(controller.signal),
            ]);
            if (controller.signal.aborted) return;

            if (areasR.status === 'fulfilled') setAreas(areasR.value);
            else console.error('Error cargando áreas:', areasR.reason);

            const defectos = defectosR.status === 'fulfilled' ? defectosR.value : [];
            if (defectosR.status === 'fulfilled') setCatalogoDefectos(defectos);
            else console.error('Error cargando catálogo de defectos:', defectosR.reason);

            if (causasR.status === 'fulfilled') setCatalogoCausas(causasR.value);
            else console.error('Error cargando catálogo de causas raíz:', causasR.reason);

            if (partesR.status === 'fulfilled') setCatalogoPartes(partesR.value);
            else console.error('Error cargando catálogo de partes:', partesR.reason);

            try {
                const pares = await calidadApi.getParteDefectoPairs(defectos, controller.signal);
                if (!controller.signal.aborted) setParteDefectoPairs(pares);
            } catch (err) {
                if (err.name !== 'AbortError') console.error('Error cargando relación parte-defecto:', err);
            } finally {
                if (!controller.signal.aborted) setLoadingCatalogos(false);
            }
        }
        cargarInicial();
        return () => controller.abort();
    }, []);

    // ── Cargar líneas cuando cambia área ─────────────────────────────────
    useEffect(() => {
        if (!selectedArea) {
            setLineas([]);
            return undefined;
        }
        const controller = new AbortController();
        calidadApi
            .getLineas(selectedArea.area, controller.signal)
            .then((data) => {
                if (!controller.signal.aborted) setLineas(data);
            })
            .catch((err) => {
                if (err.name !== 'AbortError') console.error('Error cargando líneas:', err);
                if (!controller.signal.aborted) setLineas([]);
            });
        return () => controller.abort();
    }, [selectedArea]);

    // ── Cargar corridas cuando cambia línea o fecha ────────────────────
    useEffect(() => {
        if (!selectedLinea) {
            setCorridas([]);
            return undefined;
        }
        const controller = new AbortController();
        setLoadingCorridas(true);
        calidadApi
            .getCorridas({ linea: selectedLinea.id, fecha: selectedFecha }, controller.signal)
            .then((data) => {
                if (!controller.signal.aborted) setCorridas(data);
            })
            .catch((err) => {
                if (err.name !== 'AbortError') console.error('Error cargando corridas:', err);
                if (!controller.signal.aborted) setCorridas([]);
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoadingCorridas(false);
            });
        return () => controller.abort();
    }, [selectedLinea, selectedFecha]);

    // ── Cargar registros de calidad cuando cambia corrida ────────────────
    const loadRegistros = useCallback(async (cId) => {
        if (!cId) {
            setRegistros([]);
            return;
        }
        setLoadingRegistros(true);
        try {
            const data = await calidadApi.getRegistros(cId);
            if (mounted.current) setRegistros(data);
        } catch (err) {
            console.error('Error cargando registros de calidad:', err);
            if (mounted.current) setRegistros([]);
        } finally {
            if (mounted.current) setLoadingRegistros(false);
        }
    }, []);

    // ── Saneamiento: si la corrida seleccionada desaparece de la lista
    // (ej. al cambiar la fecha), se deselecciona en vez de quedar fantasma ──
    useEffect(() => {
        setSelectedCorridaState((prev) => {
            if (!prev) return prev;
            const sigueExistiendo = corridas.some((c) => String(c.corrida ?? c.id) === String(prev.corrida ?? prev.id));
            if (sigueExistiendo) return prev;
            setRegistros([]);
            return null;
        });
    }, [corridas]);

    // ── Setters con cascada de limpieza ──────────────────────────────────
    const setSelectedArea = useCallback((area) => {
        setSelectedAreaState(area);
        setSelectedLineaState(null);
        setLineas([]);
        setSelectedCorridaState(null);
        setCorridas([]);
        setRegistros([]);
    }, []);

    const setSelectedLinea = useCallback((linea) => {
        setSelectedLineaState(linea);
        setSelectedCorridaState(null);
        setCorridas([]);
        setRegistros([]);
    }, []);

    const setSelectedCorrida = useCallback(
        (corrida) => {
            setSelectedCorridaState(corrida);
            if (corrida) {
                loadRegistros(corrida.corrida ?? corrida.id);
            } else {
                setRegistros([]);
            }
        },
        [loadRegistros]
    );

    // ── Autoselección de la corrida en curso cuando la fecha es hoy ──────
    const autoSelRef = useRef(null);
    useEffect(() => {
        if (loadingCorridas || !corridas.length || selectedCorrida || !selectedLinea) return;
        if (!esMismoDia(selectedFecha, new Date())) return;
        const clave = `${selectedLinea.id}|${selectedFecha.toDateString()}`;
        if (autoSelRef.current === clave) return;
        const enCurso = corridas.find((c) => c.enCurso);
        if (enCurso) {
            autoSelRef.current = clave;
            setSelectedCorrida(enCurso);
        }
    }, [corridas, loadingCorridas, selectedFecha, selectedLinea, selectedCorrida, setSelectedCorrida]);

    // ── Cascada Parte → Defectos válidos ──────────────────────────────────
    const parteDefectoMap = useMemo(() => {
        const m = new Map();
        parteDefectoPairs.forEach(({ parte, defecto }) => {
            if (!m.has(parte)) m.set(parte, new Set());
            m.get(parte).add(defecto);
        });
        return m;
    }, [parteDefectoPairs]);

    const defectosDeParte = useCallback(
        (parteCodigo) => {
            if (parteCodigo === '' || parteCodigo === null || parteCodigo === undefined) return [];
            const permitidos = parteDefectoMap.get(parteCodigo);
            if (!permitidos || permitidos.size === 0) return catalogoDefectos;
            return catalogoDefectos.filter((d) => permitidos.has(d.codigo));
        },
        [parteDefectoMap, catalogoDefectos]
    );

    // ── CRUD ─────────────────────────────────────────────────────────────
    const abrirNuevo = useCallback(() => {
        setEditTarget(null);
        setErrorGuardado(null);
        setResultadoParcial(null);
        setFormOpen(true);
    }, []);

    const abrirEditar = useCallback((registro) => {
        setEditTarget(registro);
        setErrorGuardado(null);
        setResultadoParcial(null);
        setFormOpen(true);
    }, []);

    const cerrarForm = useCallback(() => {
        setFormOpen(false);
        setEditTarget(null);
        setErrorGuardado(null);
        setResultadoParcial(null);
    }, []);

    const limpiarResultadoParcial = useCallback(() => setResultadoParcial(null), []);

    // Alta: registra N líneas de un reporte (cabecera Serie/Lote compartida).
    // Devuelve boolean — el modal decide qué hacer según el resultado en vez
    // de inferirlo del cierre del formulario.
    const guardarReporte = useCallback(
        async ({ loteoSerie, lineas }) => {
            setGuardando(true);
            setErrorGuardado(null);
            setResultadoParcial(null);
            try {
                const { folios, fallidas } = await calidadApi.crearReporte({
                    corrida: corridaId,
                    linea: selectedLinea?.id,
                    loteoSerie,
                    lineas,
                });
                await loadRegistros(corridaId);
                if (fallidas.length > 0) {
                    setResultadoParcial({ folios, fallidas });
                    setErrorGuardado(
                        `Se registraron ${folios.length} de ${folios.length + fallidas.length} defectos. Revisa las líneas restantes.`
                    );
                    return false;
                }
                setFormOpen(false);
                setEditTarget(null);
                return true;
            } catch (err) {
                setErrorGuardado(err.message || 'Error al guardar. Intenta de nuevo.');
                return false;
            } finally {
                if (mounted.current) setGuardando(false);
            }
        },
        [corridaId, selectedLinea, loadRegistros]
    );

    // Edición: actualiza un único folio existente.
    const actualizarRegistro = useCallback(
        async (folio, linea, loteoSerie) => {
            setGuardando(true);
            setErrorGuardado(null);
            try {
                await calidadApi.actualizarRegistro(folio, { ...linea, loteoSerie });
                await loadRegistros(corridaId);
                setFormOpen(false);
                setEditTarget(null);
                return true;
            } catch (err) {
                setErrorGuardado(err.message || 'No se pudo actualizar el registro.');
                return false;
            } finally {
                if (mounted.current) setGuardando(false);
            }
        },
        [corridaId, loadRegistros]
    );

    const eliminarRegistro = useCallback(
        async (folio) => {
            setErrorAccion(null);
            try {
                await calidadApi.eliminarRegistro(folio);
                await loadRegistros(corridaId);
                return true;
            } catch (err) {
                console.error('Error eliminando registro:', err);
                setErrorAccion(err.message || 'No se pudo eliminar el registro.');
                return false;
            }
        },
        [corridaId, loadRegistros]
    );

    // ── Resumen calculado ────────────────────────────────────────────────
    const resumen = useMemo(
        () => calcularResumen(registros, catalogoDefectos),
        [registros, catalogoDefectos]
    );

    const value = useMemo(
        () => ({
            selectedFecha,
            setSelectedFecha,
            areas,
            lineas,
            corridas,
            loadingCorridas,
            selectedArea,
            setSelectedArea,
            selectedLinea,
            setSelectedLinea,
            selectedCorrida,
            setSelectedCorrida,
            catalogoDefectos,
            catalogoCausas,
            catalogoPartes,
            loadingCatalogos,
            defectosDeParte,
            registros,
            loadingRegistros,
            resumen,
            formOpen,
            editTarget,
            guardando,
            errorGuardado,
            errorAccion,
            resultadoParcial,
            limpiarResultadoParcial,
            abrirNuevo,
            abrirEditar,
            cerrarForm,
            guardarReporte,
            actualizarRegistro,
            eliminarRegistro,
        }),
        [
            selectedFecha,
            areas,
            lineas,
            corridas,
            loadingCorridas,
            selectedArea,
            setSelectedArea,
            selectedLinea,
            setSelectedLinea,
            selectedCorrida,
            setSelectedCorrida,
            catalogoDefectos,
            catalogoCausas,
            catalogoPartes,
            loadingCatalogos,
            defectosDeParte,
            registros,
            loadingRegistros,
            resumen,
            formOpen,
            editTarget,
            guardando,
            errorGuardado,
            errorAccion,
            resultadoParcial,
            limpiarResultadoParcial,
            abrirNuevo,
            abrirEditar,
            cerrarForm,
            guardarReporte,
            actualizarRegistro,
            eliminarRegistro,
        ]
    );

    return <CalidadContext.Provider value={value} {...props} />;
}

export function useCalidad() {
    const context = useContext(CalidadContext);
    if (!context) throw new Error('useCalidad debe estar dentro del proveedor CalidadContext');
    return context;
}
